import { toCamelCase } from 'shared/helpers/utils'
import { Organization } from 'shared/models/organization'

import axios from './companyHttp'

export interface OrganizationTeam {
  id: string
  name: string
  memberCount: number
  driverCount: number
  truckCount: number
  trailerCount: number
}

export interface OrganizationTeams {
  count: number
  next: string | null
  previous: string | null
  results: OrganizationTeam[]
}

export interface RateconInbox {
  address: string
  enabled: boolean
  checking: boolean
  checkRequestedAt: string | null
  nextCheckAt: string | null
  lastCheckedAt: string | null
  lastReceivedAt: string | null
  lastError: string
}

export async function getRateconInbox(id: string): Promise<RateconInbox> {
  const response = await axios.get(`/api/v3/organizations/${id}/ratecon-inbox/`)
  return toCamelCase(response.data) as RateconInbox
}

export async function checkRateconInbox(id: string): Promise<RateconInbox> {
  const response = await axios.post(`/api/v3/organizations/${id}/ratecon-inbox/check/`, {})
  return toCamelCase(response.data) as RateconInbox
}

export async function getRateconAddress(id: string): Promise<Pick<RateconInbox, 'address' | 'enabled'>> {
  return (await axios.get(`/api/v3/organizations/${id}/ratecon-address/`)).data
}

export async function getOrganization(id: string) {
  const response = await axios.get(`/api/v3/organizations/${id}/`)
  return toCamelCase(response.data) as Organization
}

export async function checkOrganizationAuthority(id: string) {
  const response = await axios.post(`/api/v3/organizations/${id}/authority/`, {})
  return toCamelCase(response.data) as Organization
}

export interface OrganizationSettingsInput {
  driver_metrics_enabled?: boolean
  driver_revenue_enabled?: boolean
  name?: string
  legal_name?: string
  contact_email?: string
  dot_number?: string | null
  mc_number?: string | null
  expected_date_updated: string
}

export class OrganizationSaveError extends Error {
  constructor(
    message: string,
    public fieldErrors: Record<string, string> = {},
    public conflict = false
  ) {
    super(message)
  }
}

export async function updateOrganization(id: string, input: OrganizationSettingsInput) {
  try {
    const response = await axios.patch(`/api/v3/organizations/${id}/`, input)
    return toCamelCase(response.data) as Organization
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409) {
        throw new OrganizationSaveError(
          'Company settings changed since you opened this form. Reload the latest details before saving.',
          {},
          true
        )
      }
      if (error.response?.status === 403 || error.response?.status === 404) {
        throw new OrganizationSaveError('You no longer have permission to edit this company.')
      }
      if (error.response?.status === 400) {
        const fields: Record<string, string> = {}
        const data = error.response.data
        for (const key of ['name', 'legal_name', 'contact_email', 'dot_number', 'mc_number']) {
          const messages = data?.[key]
          if (Array.isArray(messages) && messages.every((message) => typeof message === 'string')) {
            fields[key] = messages.join(' ')
          } else if (typeof messages === 'string') {
            fields[key] = messages
          }
        }
        throw new OrganizationSaveError(
          'Unable to save company settings. Check the details and try again.',
          fields
        )
      }
    }
    throw new OrganizationSaveError('Unable to save company settings. Please try again.')
  }
}

export async function getOrganizationTeams(id: string, page: number, search: string, pageSize: number) {
  const response = await axios.get(`/api/v3/organizations/${id}/teams/`, {
    params: { page, page_size: pageSize, search },
  })
  return toCamelCase(response.data) as OrganizationTeams
}

export async function getCompanyBranding(id: string) {
  const result = await axios.get(`/api/v1/branding/${id}/`)
  return toCamelCase(result.data) as import('shared/components/CompanyBrand/CompanyBrand').CompanyBranding
}

export async function saveCompanyLogo(
  id: string,
  file: File | null,
  revision: string,
  slot: 'company' | 'sidebar' = 'company'
) {
  const data = new FormData()
  data.append('expected_date_updated', revision)
  if (file) data.append('logo', file)
  else data.append('remove', 'true')
  try {
    const result = await axios.post(
      `/api/v3/organizations/${id}/${slot === 'sidebar' ? 'sidebar-logo' : 'logo'}/`,
      data
    )
    return toCamelCase(result.data) as Organization
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409)
        throw new OrganizationSaveError('Company settings changed. Reload before saving your logo.', {}, true)
      if ([403, 404].includes(error.response?.status || 0))
        throw new OrganizationSaveError('You no longer have permission to edit this company.')
      const message = error.response?.data?.logo
      if (typeof message === 'string' || Array.isArray(message))
        throw new OrganizationSaveError(Array.isArray(message) ? message.join(' ') : message)
    }
    throw new OrganizationSaveError('Unable to save the company logo. Please try again.')
  }
}
